/**
 * Pure derivations for the orders destination: the order card's row, ticket
 * status/props, money and time formatting, the orders search, and the
 * invoice's stored URL.
 *
 * `orderStatusTone`/`orderStatusLabel` render status as plain typography
 * rather than a badge: "paid" is the healthy default and stays plain ink,
 * so color is spent only where an order genuinely needs a second look
 * (refunded, cancelled, a hard-bounced email) instead of on every row alike.
 *
 * No per-line receipt derivation lives here: the order sheet summarises the
 * invoice by its number and total and opens the § 14 PDF for the breakdown,
 * which is where that breakdown is already printed to the law's own
 * requirements.
 */

import { Order, Ticket, type TicketType } from "@momo/types";

import { uiLanguage } from "../../i18n/localize.ts";
import type { OrderRow } from "./console.state.ts";

export type { OrderRow } from "./console.state.ts";
export type TicketRow = Ticket.Ticket & { readonly id: string };
export type TypeRow = TicketType.TicketType & { readonly id: string };

export type Tone = "default" | "warning" | "error";

export function orderStatusTone(status: Order.Order["status"]): Tone {
	if (status === "cancelled") return "error";
	if (status === "refunded") return "warning";
	return "default";
}

export function orderStatusLabel(status: Order.Order["status"]): string {
	switch (status) {
		case "authorised":
			return "Authorised — payment not yet captured";
		case "paid":
			return "Paid";
		case "refunded":
			return "Refunded";
		case "cancelled":
			return "Cancelled";
	}
}

export function formatCents(cents: number): string {
	return new Intl.NumberFormat(uiLanguage() === "de" ? "de-DE" : "en-GB", {
		style: "currency",
		currency: "EUR",
	}).format(cents / 100);
}

export function orderTotal(order: OrderRow): number {
	return order.lines.reduce((sum, line) => sum + line.amount, 0);
}

/** How many admissions the order bought. Only a ticket line is bought in a
 *  quantity — a booking fee and a discount carry none — so the count narrows
 *  positively onto that branch. */
export function orderTicketCount(order: OrderRow): number {
	return order.lines.reduce(
		(sum, line) => (line.kind === "ticket" ? sum + line.qty : sum),
		0,
	);
}

export function relativeAgo(date: Date, locale: "en" | "de"): string {
	const seconds = Math.round((Date.now() - date.getTime()) / 1000);
	const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
	if (seconds < 60) return rtf.format(-seconds, "second");
	const minutes = Math.round(seconds / 60);
	if (minutes < 60) return rtf.format(-minutes, "minute");
	const hours = Math.round(minutes / 60);
	if (hours < 24) return rtf.format(-hours, "hour");
	const days = Math.round(hours / 24);
	if (days < 7) return rtf.format(-days, "day");
	const weeks = Math.round(days / 7);
	if (weeks < 5) return rtf.format(-weeks, "week");
	const months = Math.round(days / 30);
	return rtf.format(-months, "month");
}

/**
 * The messenger tick the email line carries — the same read-receipt vocabulary
 * a buyer already knows from every chat app, spent on the one fact an operator
 * checks a card for: did this person actually get their tickets.
 *
 * Two grey ticks delivered, two blue ticks opened — WhatsApp's own double-check
 * grammar: delivered and opened share the `done_all` glyph and differ only by
 * tone, so "they have seen it" is legible without reading a word. `schedule`
 * rather than a hollow tick for the unconfirmed
 * state: Lettermint has accepted the send but reported nothing back, and a
 * greyed tick would claim more than that.
 *
 * `labelKey` resolves under `admin.events.console.` — the label is this icon's
 * accessible name, not decoration, so it is localized at the view rather than
 * baked in here.
 */
export type DeliveryTick = {
	readonly icon: "schedule" | "check" | "done_all" | "error";
	readonly toneClass: string;
	readonly labelKey:
		| "deliveryPending"
		| "deliveryDelivered"
		| "deliveryOpened"
		| "deliveryBounced";
	/** The one state an operator must act on — also what floats a card to the
	 *  top of the orders list and puts the `error` art overlay on it. */
	readonly bounced: boolean;
};

const DELIVERY_TICKS: Record<Order.DeliveryStatus, DeliveryTick> = {
	unknown: {
		icon: "schedule",
		toneClass: "text-surface-onVariant",
		labelKey: "deliveryPending",
		bounced: false,
	},
	delivered: {
		icon: "done_all",
		toneClass: "text-surface-onVariant",
		labelKey: "deliveryDelivered",
		bounced: false,
	},
	opened: {
		icon: "done_all",
		toneClass: "text-primary-default",
		labelKey: "deliveryOpened",
		bounced: false,
	},
	hard_bounced: {
		icon: "error",
		toneClass: "text-error-default",
		labelKey: "deliveryBounced",
		bounced: true,
	},
};

export function deliveryTick(order: OrderRow): DeliveryTick {
	return DELIVERY_TICKS[order.emailDeliveryStatus];
}

export type OrderCardRow = {
	readonly buyerName: string;
	readonly buyerEmail: string;
	readonly statusLabel: string | null;
	readonly statusTone: Tone;
	readonly delivery: DeliveryTick;
	/** order.origin === 'guest' — a comped order the console issued rather
	 *  than a sale the buyer paid for. Carried onto the row so the card can
	 *  render its own visual distinction without reaching back into `order`. */
	readonly isGuest: boolean;
	readonly ticketCount: number;
	readonly amountLabel: string;
	/** Order.feeCents formatted — null while the provider hasn't reported,
	 *  which is not €0.00: an empty report is a transaction that genuinely
	 *  cost nothing and does carry the label. */
	readonly feeLabel: string | null;
	readonly whenLabel: string;
};

export function orderCardRow(order: OrderRow): OrderCardRow {
	const tone = orderStatusTone(order.status);
	const feeCents = Order.feeCents(order);
	return {
		buyerName: order.buyerName,
		buyerEmail: order.buyerEmail,
		statusLabel: tone === "default" ? null : orderStatusLabel(order.status),
		statusTone: tone,
		delivery: deliveryTick(order),
		isGuest: order.origin === "guest",
		ticketCount: orderTicketCount(order),
		amountLabel: formatCents(orderTotal(order)),
		feeLabel: feeCents === null ? null : formatCents(feeCents),
		whenLabel: relativeAgo(order.authorisedAt.toDate(), "en"),
	};
}

/**
 * Shared by the Orders tab and guest-issue.view.ts's own search: a plain
 * case-insensitive substring match over buyer name and email, preserving
 * `orders`' own order rather than re-sorting by match strength. No
 * German-fold or fuzzy scoring: that earns its keep on the door's stressed,
 * one-shot phone lookup (manual-lookup.state.ts's `rankOrders`), which a
 * desk admin typing a known name or email in front of them doesn't share.
 *
 * The third leg matches the `T-######` reference the buyer holds. It is a
 * one-way digest of the ticket doc id, so a query is never reversed into an
 * id: each of the order's own already-loaded tickets derives its reference
 * and is compared. `ticketsByOrder` is passed in rather than read from state
 * so this stays pure; a caller with no ticket rows keeps the name/email
 * search it already had.
 */
const TICKET_REFERENCE = /^T?(\d{3,})$/;

export function filterOrdersByQuery(
	query: string,
	orders: readonly OrderRow[],
	ticketsByOrder: ReadonlyMap<string, readonly TicketRow[]> = new Map(),
): readonly OrderRow[] {
	const q = query.trim().toLowerCase();
	if (!q) return orders;
	// Only a query shaped like a reference enters the ticket leg — a name
	// fragment would otherwise sweep the list through it. The digit run is
	// matched as a suffix, so a partial tail, the bare six digits, and both
	// spellings of the full reference all land on `T-000123`.
	const digits =
		TICKET_REFERENCE.exec(query.toUpperCase().replace(/[^A-Z0-9]/g, ""))?.[1] ??
		null;
	return orders.filter(
		(o) =>
			o.buyerName.toLowerCase().includes(q) ||
			o.buyerEmail.toLowerCase().includes(q) ||
			(digits !== null &&
				(ticketsByOrder.get(o.id) ?? []).some((t) =>
					Ticket.ticketDisplayId(t.id).endsWith(digits),
				)),
	);
}

/**
 * The orders destination's facet filter — three closed lenses over the
 * already-loaded order set: comped issuances, sales, and refunds. `""` (all)
 * is the group's own deselected state rather than a fourth facet value —
 * `<schmancy-chips>`'s single-select already clears back to it when the
 * active chip is clicked again, so there is no "all" chip to render.
 */
export type OrderFacet = "guest" | "sale" | "refunded";

export function filterOrdersByFacet(
	facet: OrderFacet | "",
	orders: readonly OrderRow[],
): readonly OrderRow[] {
	if (facet === "guest") return orders.filter((o) => o.origin === "guest");
	if (facet === "sale") return orders.filter((o) => o.origin === "sale");
	if (facet === "refunded")
		return orders.filter((o) => o.status === "refunded");
	return orders;
}

/**
 * Bounds by `authorisedAt` — the order's own birth stamp, the one date field
 * every order carries unconditionally regardless of origin or status
 * (`paidAt` is null on a guest order), and the same value each card's own
 * "when" label already reads, so the range an operator sets matches what
 * the list shows. `start`/`end` are `schmancy-date-range`'s own naive local
 * `YYYY-MM-DD` strings; `end` is inclusive through the end of that day.
 */
export function filterOrdersByDateRange(
	start: string | null,
	end: string | null,
	orders: readonly OrderRow[],
): readonly OrderRow[] {
	if (!start && !end) return orders;
	const startMs = start ? new Date(`${start}T00:00:00`).getTime() : null;
	const endMs = end ? new Date(`${end}T23:59:59.999`).getTime() : null;
	return orders.filter((o) => {
		const authorisedMs = o.authorisedAt.toDate().getTime();
		if (startMs !== null && authorisedMs < startMs) return false;
		if (endMs !== null && authorisedMs > endMs) return false;
		return true;
	});
}

/**
 * Each chip's own live count — scoped to the date range alone, never
 * further narrowed by the OTHER two facets, so "Guest 4 / Sale 128 /
 * Refunded 3" stay three comparable slices of the same set and only move
 * together when the date range itself changes.
 */
export function facetCounts(
	dateFiltered: readonly OrderRow[],
): Record<OrderFacet, number> {
	return {
		guest: dateFiltered.filter((o) => o.origin === "guest").length,
		sale: dateFiltered.filter((o) => o.origin === "sale").length,
		refunded: dateFiltered.filter((o) => o.status === "refunded").length,
	};
}

/**
 * The stored § 14 invoice as a fetchable URL, or null for an order that has
 * none. Built rather than fetched: `fulfillReceipt` stores the document at
 * `files/{orderAccessToken}/receipt.pdf` and no Admin-SDK write mints a
 * download token for it, so there is nothing to retrieve and
 * `getDownloadURL`'s round-trip buys nothing. Authorization is the path
 * itself — storage.rules' own `files/{orderAccessToken}` block allows `get`
 * by path possession, the same capability the buyer's own order page uses.
 *
 * `invoiceNumber` — not the access token — is what says an invoice exists at
 * all. Fulfillment mints a token for every order it runs for, comped ones
 * included, but `fulfillReceipt` returns `skippedFree` on an order whose
 * lines sum to €0: no consideration, no § 14 obligation, so it consumes no
 * number and stores no PDF. Reading the token alone would hand every comp a
 * URL to an object that was never written.
 *
 * A present URL is still not proof the object exists: the receipt task may
 * be mid-flight between minting its number and storing the document, or may
 * have failed. The caller renders the absence it can prove (no number) and
 * lets the document surface report its own.
 */
export function invoiceUrl(order: OrderRow): string | null {
	if (!order.invoiceNumber || !order.orderAccessToken) return null;
	return order.receiptUrl ?? null;
}
